import React, { useMemo, useRef, useCallback, useState } from "react";
import { InfiniteQueryResult, QueryKey } from "react-query";
import { useTranslation } from "react-i18next";
import { AgGridReactProps } from "@ag-grid-community/react";
import { GridApi, RowNode } from "@ag-grid-community/core";

import { DataGrid } from "fe-shared/src/components";
import ExportExcelData from "fe-shared/src/components/ExportExcelData";
import {
  DataGridFilters,
  DataGridOptions,
} from "fe-shared/src/components/DataGrid/modules/types";
import { BusinessRole } from "fe-shared/src/types/domain";

import { TransferResponse, TransferListItem } from "../../types";
import {
  getTransfersArray,
  getRequestParams,
} from "../../helpers/transfersConverter";
import { getColumnDefs, getRenderers } from "../../helpers/transfersGridConfig";
import { CreateInvoiceButton } from "..";

import { getTransfers } from "./modules/api";
import useTransfersExport from "../../hooks/useTransfersExport";

import styles from "./TransfersDataGrid.module.scss";

interface Props extends AgGridReactProps {
  transfers: InfiniteQueryResult<TransferResponse<TransferListItem>>;
  transfersQueryKey: QueryKey;
  lastOpenedTransfersNumber: string | null;
  clearLastOpenedTransfersNumber: () => void;
  options: DataGridOptions;
  filters: DataGridFilters;
  setOptions: (options: DataGridOptions) => void;
  setFilters: (filters: DataGridFilters) => void;
  isInternal?: boolean;
  businessRoleCode?: BusinessRole;
  canExport?: boolean;
  displayName?: string;
  canCreateInvoice?: boolean;
  companyId?: number;
}

const TransfersDataGrid = (props: Props) => {
  const {
    lastOpenedTransfersNumber,
    clearLastOpenedTransfersNumber,
    transfers,
    transfersQueryKey,
    filters,
    isInternal = false,
    businessRoleCode,
    canExport = false,
    displayName,
    canCreateInvoice,
    companyId,
    ...otherProps
  } = props;

  const { t } = useTranslation();
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectedRows, setSelectedRows] = useState<TransferListItem[]>([]);
  const gridApiRef = useRef<GridApi | null>(null);
  const isSelectionAvailable =
    !isInternal &&
    !!canCreateInvoice &&
    businessRoleCode === BusinessRole.Processor;
  const columnDefs = useMemo(
    () => getColumnDefs(t, isInternal, isSelectionAvailable, businessRoleCode),
    [t, isInternal, isSelectionAvailable, businessRoleCode]
  );
  const renderers = useMemo(() => getRenderers(isInternal), [isInternal]);
  const requestParams = getRequestParams(filters, otherProps.options);
  const { columnConfig, getDataRows } = useTransfersExport(
    requestParams,
    companyId
  );

  const assignGridApi = (api: GridApi) => {
    gridApiRef.current = api;
  };

  const onFirstDataRendered = useCallback(() => {
    if (lastOpenedTransfersNumber) {
      gridApiRef.current!.ensureNodeVisible(
        (node: RowNode) => node.id === lastOpenedTransfersNumber.toString(),
        "middle"
      );

      clearLastOpenedTransfersNumber();
    }
  }, [lastOpenedTransfersNumber, clearLastOpenedTransfersNumber]);

  const rowCount = transfers.data?.[0].total;

  const transfersCompanies = useMemo(() => getTransfersArray(transfers.data), [
    transfers.data,
  ]);

  const isCreateInvoiceEnabled =
    selectedRows.length > 0 && selectedRows.every((row) => row.isInvoiceable);

  const isShowInvoiceableApplied = filters?.isInvoiceable?.value === "true";

  const setIsShowInvoiceable = () => {
    gridApiRef.current!.setFilterModel({
      ...filters,
      isInvoiceable: {
        type: "textFilter",
        value: isShowInvoiceableApplied ? "" : "true",
      },
    });
  };

  const getTransferNumbers = async () => {
    if (isAllSelected && selectedRows.length < rowCount!) {
      return getTransfers("", requestParams, companyId, null).then((res) =>
        res.transfers.map((tr) => tr.transferNumber)
      );
    }
    return Promise.resolve(selectedRows.map((tr) => tr.transferNumber));
  };

  return (
    <main className={styles.container}>
      <div className={styles.headerBlock}>
        <h1 className={styles.header}>
          {t("transfers.grid.title")}
          {!!rowCount && <span className={styles.totalCount}>{rowCount}</span>}
        </h1>
        <div className={styles.buttons}>
          {canExport && (
            <ExportExcelData
              dataRows={[]}
              getDataRows={getDataRows}
              columnConfig={columnConfig}
              buttonId="export-transfers-button"
              fileName={t("transfers.grid.export.fileName")}
              buttonLabel={t("transfers.grid.export.buttonLabel")}
            />
          )}
          {isSelectionAvailable && (
            <CreateInvoiceButton
              canCreateInvoice={canCreateInvoice!}
              businessRoleCode={businessRoleCode!}
              displayName={displayName!}
              listQueryKey={transfersQueryKey}
              getItemNumbers={getTransferNumbers}
              setIsShowInvoiceable={setIsShowInvoiceable}
              isShowInvoiceableApplied={isShowInvoiceableApplied}
              isDisabled={!isCreateInvoiceEnabled}
            />
          )}
        </div>
      </div>
      <DataGrid
        idField="transferId"
        rowSelection="multiple"
        suppressRowClickSelection
        suppressCellSelection
        assignGridApi={assignGridApi}
        onFirstDataRendered={onFirstDataRendered}
        columnDefs={columnDefs}
        isLoading={transfers.isLoading}
        rowData={transfersCompanies}
        fetchMore={transfers.fetchMore}
        canFetchMore={transfers.canFetchMore}
        isFetchingMore={transfers.isFetchingMore}
        className={styles.transfersDataGrid}
        frameworkComponents={renderers}
        filters={filters}
        onSelectionChanged={(e) => {
          const rows = e.api.getSelectedRows();

          setSelectedRows(rows);
          setIsAllSelected(
            !!rows.length && rows.length === transfersCompanies?.length
          );
        }}
        {...otherProps}
      />
    </main>
  );
};

export default TransfersDataGrid;
